import { Injectable } from '@angular/core';
import { Preferences } from '@capacitor/preferences';
import { PosthogService } from './posthog.service';
import { FirebaseAnalyticsService } from './firebase-analytics.service';

export type OnboardingVariant = 'control' | 'test';
export type OnboardingDiet = 'vegan' | 'vegetarian' | 'working';
export type OnboardingStepId =
  | 'diet'
  | 'since'
  | 'activism'
  | 'pets'
  | 'gender'
  | 'interest'
  | 'name'
  | 'age'
  | 'city'
  | 'auth';

export interface OnboardingCollectedData {
  diet?: OnboardingDiet;
  since?: string;
  activism?: string;
  pets?: string;
  gender?: string;
  genderDescription?: string;
  interest?: string;
  interest2?: string[];
  name?: string;
  age?: number;
  city?: string;
  location?: { lat: number; lng: number };
}

export interface OnboardingProgress {
  variant: OnboardingVariant;
  abTestParticipant: true;
  startedAt: string;
  lastUpdatedAt: string;
  currentStep: OnboardingStepId;
  stepsCompleted: string[];
  data: OnboardingCollectedData;
}

export interface OnboardingStep {
  id: OnboardingStepId;
  required: boolean | ((data: OnboardingCollectedData) => boolean);
  skipIf?: (data: OnboardingCollectedData) => boolean;
}

export const ONBOARDING_STORAGE_KEY = 'ONBOARDING_PROGRESS_V2';
export const ONBOARDING_PENDING_PHOTO_KEY = 'ONBOARDING_PENDING_PHOTO_SETUP';
export const ONBOARDING_FLAG_KEY = 'onboarding-flow-v2';

export const ONBOARDING_STEPS: OnboardingStep[] = [
  { id: 'diet', required: true },
  {
    id: 'since',
    required: (data) => data.diet === 'vegan',
    skipIf: (data) => data.diet === 'working',
  },
  { id: 'activism', required: true },
  { id: 'pets', required: false },
  { id: 'gender', required: true },
  { id: 'interest', required: true },
  { id: 'name', required: true },
  { id: 'age', required: true },
  { id: 'city', required: true },
  { id: 'auth', required: true },
];

export const ONBOARDING_STEP_IDS: OnboardingStepId[] = ONBOARDING_STEPS.map((step) => step.id);

@Injectable({
  providedIn: 'root',
})
export class OnboardingService {
  private currentProgress: OnboardingProgress | null = null;
  private stepStartTimes: Record<string, number> = {};

  constructor(
    private posthog: PosthogService,
    private firebaseAnalytics: FirebaseAnalyticsService
  ) {}

  async getVariant(): Promise<OnboardingVariant> {
    try {
      const flag = await this.posthog.getFeatureFlag(ONBOARDING_FLAG_KEY);
      const variant: OnboardingVariant = flag === 'test' ? 'test' : 'control';

      console.log(`[Onboarding] Feature flag ${ONBOARDING_FLAG_KEY} =`, flag, '→', variant);

      await this.posthog.capture('onboarding_variant_assigned', {
        variant,
        flag_value: flag ?? null,
        ab_test_participant: true,
      });

      return variant;
    } catch (error) {
      console.error('[Onboarding] Feature flag check failed:', error);
      return 'control';
    }
  }

  async initializeProgress(variant: OnboardingVariant): Promise<void> {
    this.currentProgress = {
      variant,
      abTestParticipant: true,
      startedAt: new Date().toISOString(),
      lastUpdatedAt: new Date().toISOString(),
      currentStep: 'diet',
      stepsCompleted: [],
      data: {},
    };

    await this.saveProgress();

    await this.posthog.capture('onboarding_started', {
      variant,
      ab_test_participant: true,
    });
  }

  async hasProgress(): Promise<boolean> {
    const result = await Preferences.get({ key: ONBOARDING_STORAGE_KEY });
    return result.value !== null && result.value !== undefined && result.value !== '';
  }

  async hasResumableProgress(): Promise<boolean> {
    const progress = await this.loadProgress();
    return !!progress && progress.variant === 'test' && progress.stepsCompleted.length > 0;
  }

  async loadProgress(): Promise<OnboardingProgress | null> {
    if (this.currentProgress) {
      return this.currentProgress;
    }

    const result = await Preferences.get({ key: ONBOARDING_STORAGE_KEY });
    if (result.value) {
      this.currentProgress = JSON.parse(result.value);
      return this.currentProgress;
    }
    return null;
  }

  private async saveProgress(): Promise<void> {
    if (!this.currentProgress) {
      return;
    }

    this.currentProgress.lastUpdatedAt = new Date().toISOString();

    await Preferences.set({
      key: ONBOARDING_STORAGE_KEY,
      value: JSON.stringify(this.currentProgress),
    });
  }

  async saveStepData(step: string, data: OnboardingCollectedData): Promise<void> {
    if (!this.currentProgress) {
      console.error('[Onboarding] No progress to update');
      return;
    }

    this.currentProgress.data = {
      ...this.currentProgress.data,
      ...data,
    };

    if (!this.currentProgress.stepsCompleted.includes(step)) {
      this.currentProgress.stepsCompleted.push(step);
    }

    await this.saveProgress();
    this.trackStepCompleted(step);
  }

  async mergeCollectedData(data: OnboardingCollectedData): Promise<void> {
    if (!this.currentProgress) {
      return;
    }

    this.currentProgress.data = {
      ...this.currentProgress.data,
      ...data,
    };
    await this.saveProgress();
  }

  getNextStep(currentStep: string): OnboardingStepId | null {
    const currentIndex = ONBOARDING_STEPS.findIndex((s) => s.id === currentStep);

    for (let i = currentIndex + 1; i < ONBOARDING_STEPS.length; i++) {
      const step = ONBOARDING_STEPS[i];

      if (step.skipIf && this.currentProgress && step.skipIf(this.currentProgress.data)) {
        continue;
      }

      return step.id;
    }

    return null;
  }

  getPreviousStep(currentStep: string): OnboardingStepId | null {
    const currentIndex = ONBOARDING_STEPS.findIndex((s) => s.id === currentStep);

    for (let i = currentIndex - 1; i >= 0; i--) {
      const step = ONBOARDING_STEPS[i];

      if (step.skipIf && this.currentProgress && step.skipIf(this.currentProgress.data)) {
        continue;
      }

      return step.id;
    }

    return null;
  }

  async moveToNextStep(currentStep: string): Promise<OnboardingStepId | null> {
    if (!this.currentProgress) {
      return null;
    }

    const nextStep = this.getNextStep(currentStep);

    if (nextStep) {
      this.currentProgress.currentStep = nextStep;
      await this.saveProgress();
      this.trackStepViewed(nextStep);
    }

    return nextStep;
  }

  getCollectedData(): OnboardingCollectedData {
    return this.currentProgress?.data || {};
  }

  getCurrentProgress(): OnboardingProgress | null {
    return this.currentProgress;
  }

  applyCollectedData(userData: Record<string, unknown> = {}): Record<string, unknown> {
    const data = this.getCollectedData();
    if (!data || Object.keys(data).length === 0) {
      return userData;
    }

    return {
      ...userData,
      diet: data.diet ?? userData['diet'],
      since: data.since ?? userData['since'],
      activism: data.activism ?? userData['activism'],
      pets: data.pets ?? userData['pets'],
      gender2: data.gender ?? userData['gender2'],
      genderdescription: data.genderDescription ?? userData['genderdescription'],
      interest2: data.interest2 ?? userData['interest2'],
      firstName: data.name ?? userData['firstName'],
      age: data.age ?? userData['age'],
      city: data.city ?? userData['city'],
    };
  }

  async clearProgress(): Promise<void> {
    await Preferences.remove({ key: ONBOARDING_STORAGE_KEY });
    this.currentProgress = null;
    this.stepStartTimes = {};
  }

  async markPendingPhotoSetup(): Promise<void> {
    await Preferences.set({ key: ONBOARDING_PENDING_PHOTO_KEY, value: 'true' });
  }

  async hasPendingPhotoSetup(): Promise<boolean> {
    const result = await Preferences.get({ key: ONBOARDING_PENDING_PHOTO_KEY });
    return result.value === 'true';
  }

  async clearPendingPhotoSetup(): Promise<void> {
    await Preferences.remove({ key: ONBOARDING_PENDING_PHOTO_KEY });
  }

  hasRequiredProfileFields(userData: Record<string, unknown> | null | undefined): boolean {
    if (!userData) {
      return false;
    }
    const interests = userData['interest2'];
    return !!(
      userData['diet'] &&
      userData['gender2'] &&
      userData['firstName'] &&
      userData['age'] &&
      userData['city'] &&
      Array.isArray(interests) &&
      interests.length > 0
    );
  }

  trackStepViewed(step: string): void {
    this.stepStartTimes[step] = Date.now();

    const stepIndex = ONBOARDING_STEPS.findIndex((s) => s.id === step);

    void this.posthog.capture('onboarding_step_viewed', {
      variant: this.currentProgress?.variant,
      step,
      step_number: stepIndex + 1,
      ab_test_participant: true,
    });
  }

  private trackStepCompleted(step: string): void {
    const stepIndex = ONBOARDING_STEPS.findIndex((s) => s.id === step);
    const timeSpent = this.getStepDuration(step);

    void this.posthog.capture('onboarding_step_completed', {
      variant: this.currentProgress?.variant,
      step,
      step_number: stepIndex + 1,
      time_spent_seconds: timeSpent,
      ab_test_participant: true,
    });
  }

  async trackResumed(): Promise<void> {
    if (!this.currentProgress) {
      return;
    }

    await this.posthog.capture('onboarding_resumed', {
      variant: this.currentProgress.variant,
      steps_completed: this.currentProgress.stepsCompleted.length,
      last_step: this.currentProgress.currentStep,
      ab_test_participant: true,
    });
  }

  async trackAbTestEntry(variant: OnboardingVariant, entryPoint = 'create_account_button'): Promise<void> {
    await this.posthog.capture('ab_test_entry', {
      variant,
      entry_point: entryPoint,
    });
  }

  extractSoulId(soul: unknown): string | null {
    if (!soul || typeof soul !== 'object') {
      return null;
    }
    const id = (soul as { _id?: unknown })._id;
    if (typeof id === 'string' && id.length > 0) {
      return id;
    }
    if (id && typeof id === 'object') {
      const oid = (id as { $oid?: unknown }).$oid;
      if (typeof oid === 'string' && oid.length > 0) {
        return oid;
      }
    }
    return null;
  }

  /**
   * Alias the anonymous PostHog distinct id (experiment exposure) onto the
   * created user before any conversion events. Call as soon as upsert returns.
   */
  async identifySoul(soul: unknown): Promise<string | null> {
    const userId = this.extractSoulId(soul);
    if (!userId) {
      console.warn('[Onboarding] No user id on created soul; signup events stay anonymous');
      return null;
    }

    // Assign the account as soon as signup returns so subsequent web or
    // native events carry the same cross-platform GA4 User-ID.
    try {
      await this.firebaseAnalytics.setUserId(userId);
    } catch (error) {
      // Analytics must never block account creation.
      console.error('[Onboarding] Firebase Analytics identify failed:', error);
    }

    if (this.posthog.getCurrentUserId() !== userId) {
      await this.posthog.identify(userId);
      await this.posthog.flush();
    }

    return userId;
  }

  async trackSignup(authMethod: string, soul?: unknown, extra: Record<string, unknown> = {}): Promise<void> {
    await this.identifySoul(soul);
    const variant = this.currentProgress?.variant;
    await this.posthog.capture('sign_up', {
      variant,
      auth_method: authMethod,
      ab_test_participant: true,
      ...extra,
    });
  }

  private getStepDuration(step: string): number {
    const startTime = this.stepStartTimes[step];
    if (!startTime) {
      return 0;
    }
    return Math.floor((Date.now() - startTime) / 1000);
  }

  async submitToServer(_authToken: string, authMethod: string): Promise<void> {
    if (!this.currentProgress) {
      throw new Error('No onboarding data to submit');
    }

    const totalTime = Math.floor(
      (Date.now() - new Date(this.currentProgress.startedAt).getTime()) / 1000
    );

    await this.posthog.capture('onboarding_completed', {
      variant: this.currentProgress.variant,
      auth_method: authMethod,
      total_time_seconds: totalTime,
      ab_test_participant: true,
    });
  }
}
